import { TemplateRef, Type, Injectable } from '@angular/core';
import { NotificationsService, NotificationTypeEnum, WafModal, FileResponse } from '../../../modules/tcm-core.module';
import { TranslationSchema } from '../../../models/translations';
import { PermissionsService } from '../../../services/permissions.service';
import { TranslationService } from '../../../services/translation.service';
import { TcmMessageModal, TcmMessageModalComponent } from '../components/tcm-message-modal/tcm-message-modal.component';
import { ModalButtonDescriptor } from '../models/modal-button-descriptor';
import { ComponentOptions } from '../models/modal-options';
import { BaseSecureDetails } from '../security/base-secure-details/base-secure-details';
import { BaseSecureSearch } from '../security/base-secure-search/base-secure-search';
import { TcmSecureBaseComponent } from '../security/tcm-secure-base-component';
import { TcmModalService } from './tcm-modal.service';
import { FilePreviewModel } from '../models/file-preview-model';
import { FilePreviewComponent } from '../components/file-preview/file-preview.component';
import { TranslationResolver } from '../../../helpers/translation-resolver';
import { TcmBaseDetails } from '../base/tcm-base-details';
import { ReflectionHelper } from '../../../helpers/reflection.helper';
import { InterpolatableTranslationObject } from '@ngx-translate/core';

@Injectable()
export class TcmModalServiceImplementation extends TcmModalService {

    constructor(
        private wafModal: WafModal,
        private translationService: TranslationService,
        private permissionService: PermissionsService,
        private notificationService: NotificationsService) {
        super();
    }

    closeActiveModal(data?: any) {
        this.wafModal.closeActiveModal(data);
    }

    closeModalByComponentRef(componentRef: any, data?: any) {
        this.wafModal.closeModalByComponentRef(componentRef, data);
    }

    openSecureComponentInModal<T = any>(
        component: Type<BaseSecureDetails<any, any>> | Type<BaseSecureSearch<any, any>> | Type<TcmSecureBaseComponent<any, any>>,
        componentOptions: ComponentOptions,
        size: 'sm' | 'md' | 'lg',
        windowClass?: string): Promise<T> {

        if (!this.permissionService.checkAreaAccess(componentOptions.permissionsInfo.areaPermissionKey) ||
            !this.permissionService.checkEntityAccess(componentOptions.permissionsInfo.areaPermissionKey as any, componentOptions.permissionsInfo.entityPermissionKey as any)) {
            const errorMessage = this.translationService.translate(x => x.Messages.ErrorMessages.ScreenAccessDenied);

            this.notificationService.add({
                type: NotificationTypeEnum.Error,
                message: errorMessage
            });

            return null;
        }

        windowClass = windowClass ? windowClass + ' tcm-component-modal' : 'tcm-component-modal';

        const modalRef = this.wafModal.openInModal(component, size, componentOptions, windowClass);

        return modalRef.result;
    }

    openComponentInModal<T = any>(component: Type<any> | string, size: 'sm' | 'md' | 'lg', componentOptions?: {}, windowClass: string = null): Promise<T> {
        if (typeof component !== 'string') {
            if (ReflectionHelper.isSecureComponent(component)) {
                console.error('Secure components can not be used in this method');

                return Promise.reject();
            }
        }

        windowClass = windowClass ? windowClass + ' tcm-component-modal' : 'tcm-component-modal';

        const modalRef = this.wafModal.openInModal(component, size, componentOptions, windowClass);

        return modalRef.result;
    }

    showActionModalAsync<T>(
        title: ((schema: InterpolatableTranslationObject) => string) | string,
        content: ((schema: InterpolatableTranslationObject) => string) | TemplateRef<any> | string,
        size: 'sm' | 'md' | 'lg' = 'sm',
        buttons: ModalButtonDescriptor<T>[],
        templateData?: any): Promise<T> {
        return this.showMessageModalInternal('action', title, content, size, buttons, templateData);
    }

    showErrorModalAsync<T = void>(
        title: ((schema: InterpolatableTranslationObject) => string) | string,
        content: ((schema: InterpolatableTranslationObject) => string) | TemplateRef<any> | string,
        size: 'sm' | 'md' | 'lg' = 'sm',
        buttons?: ModalButtonDescriptor<T>[],
        templateData?: any): Promise<T> {
        return this.showMessageModalInternal('error', title, content, size, buttons, templateData);
    }

    showSuccessModalAsync<T = void>(
        title: ((schema: InterpolatableTranslationObject) => string) | string,
        content: ((schema: InterpolatableTranslationObject) => string) | TemplateRef<any> | string,
        size: 'sm' | 'md' | 'lg' = 'sm',
        buttons?: ModalButtonDescriptor<T>[],
        templateData?: any,
        dismissTimeout?: Promise<any>): Promise<T> {
        return this.showMessageModalInternal('success', title, content, size, buttons, templateData, dismissTimeout);
    }

    showWarningModalAsync<T = void>(
        title: ((schema: InterpolatableTranslationObject) => string) | string,
        content: ((schema: InterpolatableTranslationObject) => string) | TemplateRef<any> | string,
        size: 'sm' | 'md' | 'lg' = 'sm',
        buttons?: ModalButtonDescriptor<T>[],
        templateData?: any,
        windowClass?: string): Promise<T> {
        return this.showMessageModalInternal('warning', title, content, size, buttons, templateData, null, windowClass);
    }

    showConfirmationModalAsync(
        title: ((schema: InterpolatableTranslationObject) => string) | string,
        text: ((schema: InterpolatableTranslationObject) => string) | TemplateRef<any> | string,
        confirmText?: ((schema: TranslationSchema) => string) | string,
        cancelText?: ((schema: TranslationSchema) => string) | string,
        templateData?: any): Promise<boolean> {

        const buttons: ModalButtonDescriptor<boolean>[] = [
            {
                title: confirmText || (x => x.Messages.MessageHeadersAndActions.Yes),
                value: true,
                cssClass: 'success'
            },
            {
                title: cancelText || (x => x.Messages.MessageHeadersAndActions.No),
                value: false,
                cssClass: 'default',
                isDefault: true
            }
        ];

        return this.showWarningModalAsync(title, text, 'sm', buttons, templateData);
    }

    showFilePreviewAsync(model: FilePreviewModel | FileResponse, titleKey?: TranslationResolver): Promise<any> {

        const componentOptions = {
            model: model,
            titleKey: titleKey
        };

        return this.openComponentInModal(FilePreviewComponent, 'lg', componentOptions)
    }

    private showMessageModalInternal<T>(
        messageType: string,
        title: ((schema: InterpolatableTranslationObject) => string) | string, //title: ((schema: TranslationSchema) => string) | string,
        content: ((schema: InterpolatableTranslationObject) => string) | TemplateRef<any> | string, // content: ((schema: TranslationSchema) => string) | TemplateRef<any> | string,
        size: 'sm' | 'md' | 'lg' = 'sm',
        buttons: ModalButtonDescriptor<T>[],
        templateData?: any,
        dismissTimeout?: Promise<any>,
        windowClass?: string): Promise<T> {

        size = size || 'sm';

        // TODO: need to translate 'Close'
        buttons = buttons || [{ title: x => x.Common.SearchActionsAndDetailsActions.Close, value: null, cssClass: 'default', isDefault: true }];

        const _title = title instanceof Function ? this.translationService.translate(title) : title;

        const modalOptions: TcmMessageModal = {
            buttons: buttons,
            title: _title,
            contentData: templateData,
            messageType: messageType
        } as any;

        if (content instanceof TemplateRef) {
            modalOptions.template = content;
            modalOptions.templateData = templateData;
        }
        else if (content instanceof Function) {
            modalOptions.content = this.translationService.translate(content);
        }
        else {
            modalOptions.content = content;
        }

        buttons.filter(x => x.title instanceof Function).forEach(x => {
            x.title = this.translationService.translate(x.title as any);
        });

        windowClass = windowClass ? windowClass + ' tcm-message-modal' : 'tcm-message-modal';

        const modalRef = this.wafModal.openInModal(TcmMessageModalComponent, size, modalOptions, windowClass);

        if(dismissTimeout) {
            dismissTimeout.then(x => {
                modalRef.dismiss(x);
            }, r => {
                modalRef.dismiss(r);
            })
        };


        return modalRef.result;
    }
}